import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChangeFieldTypeResult, CreateFieldInput, FieldDefinitionDto, MergeFieldInput, MergeFieldResult, FieldType, ObjectType, UpdateFieldInput } from '@crm/shared';
import { api } from '../../api/client';

const KEY = (objectType?: ObjectType) => ['fields', objectType ?? 'all'];

export function useFields(objectType: ObjectType) {
  return useQuery({
    queryKey: KEY(objectType),
    queryFn: async () => (await api.get<FieldDefinitionDto[]>('/fields', { params: { objectType } })).data,
    staleTime: 5 * 60_000,
  });
}

/**
 * Refreshes the field lists; the returned promise (which holds the mutation pending) waits only for those.
 * Usage counts scan every record, so they are left alone unless the change can move them, and even then
 * refresh in the background instead of holding the save.
 */
function useInvalidate({ usage = false }: { usage?: boolean } = {}) {
  const qc = useQueryClient();
  return () => {
    if (usage) void qc.invalidateQueries({ queryKey: ['fields', 'usage-counts'] });
    return qc.invalidateQueries({
      queryKey: ['fields'],
      predicate: (q) => q.queryKey[1] !== 'usage-counts' && q.queryKey[1] !== 'usage',
    });
  };
}

export function useCreateField() {
  // re-adding a deleted key brings its stored values back
  const inv = useInvalidate({ usage: true });
  return useMutation({
    mutationFn: async (input: CreateFieldInput) => (await api.post<FieldDefinitionDto>('/fields', input)).data,
    onSuccess: inv,
  });
}

export function useUpdateField() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateFieldInput & { id: string }) =>
      (await api.patch<FieldDefinitionDto>(`/fields/${id}`, input)).data,
    onSuccess: inv,
  });
}

export function useDeleteField() {
  const inv = useInvalidate({ usage: true });
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/fields/${id}`)).data,
    onSuccess: inv,
  });
}

export function useReorderFields() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: async (input: { objectType: ObjectType; ids: string[] }) =>
      (await api.post<FieldDefinitionDto[]>('/fields/reorder', input)).data,
    onSuccess: inv,
  });
}

/** Records holding a value, per field id (creating or deleting a field refreshes it in the background). */
export function useFieldUsageCounts(objectType: ObjectType) {
  return useQuery({
    queryKey: ['fields', 'usage-counts', objectType],
    queryFn: async () => (await api.get<Record<string, number>>('/fields/usage', { params: { objectType } })).data,
  });
}

/** How many records hold a value in the field. */
export function useFieldUsage(id: string | undefined) {
  return useQuery({
    queryKey: ['fields', 'usage', id],
    queryFn: async () => (await api.get<{ count: number }>(`/fields/${id}/usage`)).data,
    enabled: !!id,
  });
}

export function useMergeField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: MergeFieldInput & { id: string }) =>
      (await api.post<MergeFieldResult>(`/fields/${id}/merge`, input)).data,
    // records changed everywhere (contacts, organizations, deals), not just the field list
    onSuccess: () => qc.invalidateQueries(),
  });
}

/** What changing the field to `type` would do (counts only, nothing changes). */
/** `enabled: false` once the change is applied: the field then has the new type and a preview would fail. */
export function useFieldTypePreview(id: string | undefined, type: FieldType | undefined, separator?: string, enabled = true) {
  return useQuery({
    queryKey: ['fields', 'type-preview', id, type, separator],
    queryFn: async () => (await api.post<ChangeFieldTypeResult>(`/fields/${id}/type`, { type, separator, dryRun: true })).data,
    enabled: enabled && !!id && !!type,
    gcTime: 0,
    // a new separator keeps the last preview on screen until the new one arrives
    placeholderData: keepPreviousData,
  });
}

export function useChangeFieldType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, type, separator }: { id: string; type: FieldType; separator?: string }) =>
      (await api.post<ChangeFieldTypeResult>(`/fields/${id}/type`, { type, separator })).data,
    // every stored value changes, so records everywhere are stale; previews no longer apply (the type changed)
    // (not awaited: the dialog closes as soon as the change is done, pages refresh behind it)
    onSuccess: () => {
      qc.removeQueries({ queryKey: ['fields', 'type-preview'] });
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[1] !== 'type-preview' });
    },
  });
}
